import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Message } from 'src/app/core/class/message';
import { CommonService } from 'src/app/core/services/common.service';
import { CreateContractService } from 'src/app/core/services/create-contract.service';
import { DocumentTemplateService } from 'src/app/core/services/document-template.service';
import { TemplateFileService } from 'src/app/core/services/template-file.service';
import { NegotiationFieldValue, TemplateFormService } from 'src/app/core/services/template-form.service';
import { FormData as TemplateFormData } from 'src/app/shared/models/form-data.model';
import { Field } from 'src/app/shared/models/field.model';
import { NegotiationFormComponent } from './negotiation-form/negotiation-form.component';
import { WarningComponent } from 'src/app/shared/components/warning/warning.component';
import { NegotiationDialogApi } from 'src/app/core/services/negotiation-dialog-api';
import { AnonymousSignService } from 'src/app/core/services/anonymous-sign.service';
import { AnonymousNegotiationContext } from 'src/app/core/services/anonymous-negotiation.service';

export interface UpdateInformationDialogData {
  document: any;
  enterpriseSign: any[];
  customerIds: string[];
  anonymousContext?: AnonymousNegotiationContext;
  api?: NegotiationDialogApi;
}

export const UPDATE_INFORMATION_DIALOG_CONFIG = {
  width: '96vw',
  maxWidth: '96vw',
  height: '90vh',
  autoFocus: false,
  disableClose: true
};

interface NegotiationApplySnapshot {
  fields: Field[];
  code: string;
  values: string;
  revision: number;
}

interface AppliedNegotiationData extends NegotiationApplySnapshot {
  pdf: Blob;
  filePath?: string;
}

@Component({
  selector: 'app-update-information-dialog',
  templateUrl: './update-information-dialog.component.html',
  styleUrls: ['./update-information-dialog.component.scss'],
  providers: [CreateContractService]
})
export class UpdateInformationDialogComponent implements OnInit, OnDestroy {
  public loading = true;
  public applying = false;
  public saving = false;
  public ready = false;
  public requiredFieldsComplete = false;
  public templateReady = false;
  public templateLoadError = false;
  public loadError = false;
  public previewError = false;
  public previewLoading = true;
  public pdfSrc: string;
  public language = localStorage.getItem('__language') || 'vi';
  public pageCurrent = 1;
  public zoomLevel = '100';
  public fileName = '';
  public formModel: TemplateFormData;
  private formTemplate: NegotiationFormComponent;
  private subscriptions = new Subscription();
  private objectUrl: string;
  private templateUrl: string;
  private templateId: string;
  private destroyed = false;
  private initialPreview: Subscription;
  private cancelConfirmation: MatDialogRef<WarningComponent>;
  private formRevision = 0;
  private appliedData: AppliedNegotiationData;
  public api: NegotiationDialogApi;

  get canSave(): boolean {
    return !!(!this.destroyed && this.ready && this.requiredFieldsComplete &&
      !this.applying && !this.saving && !this.previewLoading && !this.previewError &&
      this.appliedData && this.appliedData.revision === this.formRevision &&
      this.formTemplate && this.appliedData.values === this.formTemplate.getValuesSnapshot());
  }

  onFormValuesChange(): void {
    this.formRevision++;
    this.appliedData = null;
  }

  @ViewChild(NegotiationFormComponent, { static: false })
  set form(component: NegotiationFormComponent) {
    // A dynamic query can refresh when loading/PDF views change; keep the existing table's edits.
    if (this.formTemplate === component) {
      return;
    }
    this.appliedData = null;
    this.formTemplate = component;
    if (component && this.formModel) {
      component.isUpdate = true;
      component.referenceApi = this.api;
      component.setEditableCustomerIds(this.data.customerIds || []);
      component.processDataWithModel(this.formModel, this.data.enterpriseSign);
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpdateInformationDialogData,
    private dialogRef: MatDialogRef<UpdateInformationDialogComponent>,
    private dialog: MatDialog,
    private createContractService: CreateContractService,
    private documentTemplateService: DocumentTemplateService,
    private templateFileService: TemplateFileService,
    private templateFormService: TemplateFormService,
    private commonService: CommonService,
    private anonymousSignService: AnonymousSignService,
    private toastr: ToastrService,
    private message: Message
  ) {
    // Keep edits isolated from the detail screen until saving succeeds.
    this.api = data.api;
    this.data = JSON.parse(JSON.stringify({ ...data, api: undefined }));
  }

  private initializeContract(contract: any): void {
    this.data.document = contract;
    this.templateId = contract.documentTypeEnterpriseId;
    const mainFile = contract.mainContractAttachments && contract.mainContractAttachments[0];
    this.fileName = (mainFile && mainFile.fileName) || 'contract.pdf';
    this.createContractService.selectedTemplate.next({ id: this.templateId } as any);
    this.createContractService.document.next(contract);
    this.createContractService.currentStep.next(1);
    this.createContractService.contractCode.next(contract.code || '');
    this.createContractService.fieldData = contract.fields || [];
    this.createContractService.tableModel = contract.tableModel || null;
    // Match AddDocumentComponent's preparation of saved contracts before editing.
    if (contract.tableModel && Array.isArray(contract.tableModel.rows)) {
      contract.tableModel.rows.forEach((row, index) => {
        if (!row.field) {
          row.field = (row.fieldId && this.createContractService.fieldData.find(field =>
            field.fieldId === row.fieldId)) ||
            (!row.fieldId && this.createContractService.fieldData[index]) || null;
        }
      });
    }
    this.createContractService.recordsPartySign.next(this.data.enterpriseSign);
    this.createContractService.signatureData.next(contract.signatures || []);
  }

  ngOnInit(): void {
    // The detail screen already loaded contractDTO; use the dialog's isolated copy.
    const contract = this.data.document;
    if (!contract || !contract.id || !contract.documentTypeEnterpriseId) {
      this.onFormLoadError();
      this.previewLoading = false;
      return;
    }
    this.initializeContract(contract);
    const modelRequest = contract.tableModel && Array.isArray(contract.tableModel.rows)
      ? of({ data: contract.tableModel })
      : this.api ? this.api.getFormModel(this.templateId) : this.documentTemplateService.genForm(this.templateId);
    // Rendering saved fields must not depend on template files needed only for Apply.
    this.subscriptions.add(modelRequest.subscribe(model => {
      if (!model || !model.data || !Array.isArray(model.data.rows)) {
        this.onFormLoadError();
        return;
      }
      this.restoreFields(model.data.rows, contract.fields || []);
      this.formModel = model.data;
    }, error => {
      console.error('Unable to load negotiation table structure', error);
      this.onFormLoadError();
    }));

    if (contract.templateFilePath) {
      // Existing contracts already carry the source file used to merge their fields.
      this.templateUrl = contract.templateFilePath;
      this.templateReady = true;
    } else {
      // Fetch template metadata only when the saved source path is unavailable.
      this.loadPreviewTemplate();
    }

    const anonymous = this.data.anonymousContext;
    const pdfRequest = anonymous
      ? this.anonymousSignService.anonymousGetContractFileByHashKey(anonymous.hashKey, anonymous.tenantId, anonymous.pdfFilePath)
      : this.commonService.getViewPdfForDocumentSilently(contract.id);
    this.initialPreview = pdfRequest.subscribe(blob => {
      this.setPreview(blob);
    }, () => {
      this.previewLoading = false;
      this.previewError = true;
    });
    this.subscriptions.add(this.initialPreview);
  }

  private loadPreviewTemplate(): void {
    if (this.api) {
      this.subscriptions.add(this.api.getTemplateSource(this.templateId).subscribe(response => {
        this.templateUrl = response && response.data && response.data.url;
        this.templateReady = !!this.templateUrl;
        this.templateLoadError = !this.templateReady;
      }, () => this.templateLoadError = true));
      return;
    }
    this.subscriptions.add(this.documentTemplateService.getDocumentTemplateById(this.templateId).subscribe(template => {
      // The update screen keeps the saved model even when template details are unavailable.
      if (!template || !template.data) {
        this.templateLoadError = !this.templateReady;
        return;
      }
      this.createContractService.documentTemplate = template.data;
      const documentTemplateId = template.data.documentTemplateId;
      this.subscriptions.add(this.templateFileService.getTemplateFile(documentTemplateId).subscribe(files => {
        const templateFile = files && files.data && files.data[0];
        if (templateFile) {
          this.createContractService.documentFileTemplate.next(templateFile);
        }
        // The saved source file has the same priority as on the update screen.
        this.templateUrl = this.data.document.templateFilePath ||
          (templateFile && templateFile.file && templateFile.file.url);
        this.templateReady = !!this.templateUrl;
        this.templateLoadError = !this.templateReady;
      }, error => {
        console.error('Unable to load negotiation template file', error);
        this.templateLoadError = !this.templateReady;
      }));
    }, error => {
      console.error('Unable to load negotiation preview template', error);
      this.templateLoadError = !this.templateReady;
    }));
  }

  private restoreFields(rows: any[], fields: any[]): void {
    (rows || []).forEach(row => {
      const code = row.cells && row.cells.value && row.cells.value.code;
      const fieldsByCode = code ? fields.filter(item => item.fieldConfig && item.fieldConfig.code === code) : [];
      const field = (row.fieldId && fields.find(item => item.fieldId === row.fieldId)) ||
        (fieldsByCode.length === 1 && fieldsByCode[0]) || row.field;
      if (!field) {
        return;
      }
      row.fieldId = field.fieldId;
      row.field = field;
      if (row.cells && row.cells.value) {
        // The cached table can retain its previous display type after the field configuration changes.
        if (field.fieldConfig && field.fieldConfig.type && field.fieldConfig.type.code) {
          row.cells.value.type = field.fieldConfig.type;
        }
        if (field.fieldConfig && field.fieldConfig.formula !== undefined) {
          row.cells.value.formula = field.fieldConfig.formula;
        }
        row.cells.value.value = field.value;
        row.cells.value.valueId = field.valueId;
      }
      this.restoreFields(row.children, field.children || []);
    });
  }

  onFormReady(): void {
    this.loading = false;
    this.ready = true;
  }

  onFormLoadError(): void {
    this.appliedData = null;
    this.loading = false;
    this.ready = false;
    this.requiredFieldsComplete = false;
    this.loadError = true;
  }

  apply(): void {
    if (this.destroyed || !this.ready || !this.templateReady ||
        !this.templateUrl || this.applying || this.saving || !this.formTemplate) {
      return;
    }
    this.applying = true;
    this.appliedData = null;
    this.formTemplate.validate(valid => {
      if (this.destroyed) {
        return;
      }
      if (!valid) {
        this.applying = false;
        return;
      }
      let fields: Field[];
      try {
        fields = this.formTemplate.getPreviewFields();
      } catch (error) {
        console.error('Unable to export negotiation fields for PDF', error);
        this.showApplyError(null);
        return;
      }
      const application: NegotiationApplySnapshot = {
        fields: JSON.parse(JSON.stringify(fields)),
        code: this.createContractService.generateUUID(),
        values: this.formTemplate.getValuesSnapshot(),
        revision: this.formRevision
      };
      if (application.values === null) {
        this.showApplyError(null);
        return;
      }
      this.previewLoading = true;
      this.previewError = false;
      const body = {
        list: application.fields,
        url: this.templateUrl,
        code: application.code,
        documentTypeEnterpriseId: this.templateId,
        isNegotiate: true
      };
      const mergeRequest = this.api ? this.api.mergeDataToFile(body) : this.templateFormService.mergeDataToFile(body);
      this.subscriptions.add(mergeRequest.subscribe(blob => {
        this.setPreview(blob, true, application);
      }, error => {
        console.error('Unable to generate negotiation PDF', error);
        this.showApplyError(error);
      }));
    });
  }

  private isAppliedDataCurrent(application: AppliedNegotiationData): boolean {
    return !!(!this.destroyed && application && this.appliedData === application &&
      application.revision === this.formRevision && this.formTemplate &&
      application.values === this.formTemplate.getValuesSnapshot());
  }

  private toFieldValues(fields: Field[]): NegotiationFieldValue[] {
    return (fields || []).map(field => ({
      fieldId: field.fieldId,
      value: field.value == null ? null : field.value,
      valueId: field.valueId == null ? null : field.valueId,
      children: this.toFieldValues(field.children)
    }));
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    const application = this.appliedData;
    this.saving = true;
    const name = /\.pdf$/i.test(this.fileName) ? this.fileName : (this.fileName || 'contract') + '.pdf';
    // Upload exactly the PDF paired with this immutable field snapshot.
    // Retain its path so a failed update can be retried without uploading again.
    const upload: Observable<any> = application.filePath ? of({ data: { path: application.filePath } }) :
      this.api ? this.api.uploadFile(new File([application.pdf], name, { type: 'application/pdf' })) :
        this.commonService.uploadFileDocument(new File([application.pdf], name, { type: 'application/pdf' }));
    this.subscriptions.add(upload.pipe(switchMap(response => {
      if (!this.isAppliedDataCurrent(application)) {
        return throwError(null);
      }
      const path = response && response.data && response.data.path;
      if (!response || response.success === false || typeof path !== 'string' || !path.trim()) {
        return throwError(response);
      }
      application.filePath = path;
      const body = {
        contractId: this.data.document.id,
        filePath: path,
        fields: this.toFieldValues(application.fields)
      };
      return this.api ? this.api.updateForm(body) : this.templateFormService.updateNegotiationForm(body);
    })).subscribe(response => {
      if (this.destroyed) {
        return;
      }
      if (!response || response.success !== true) {
        this.showSaveError(response);
        return;
      }
      this.toastr.success(this.message.getMessage('enterprise_contract.update_information_dialog.save_success'));
      this.dialogRef.close(true);
    }, error => this.showSaveError(error)));
  }

  private showSaveError(error: any): void {
    if (!this.destroyed) {
      this.saving = false;
      this.notifyError(error, 'enterprise_contract.update_information_dialog.save_error');
    }
  }

  private setPreview(blob: Blob, fromApply = false, application?: NegotiationApplySnapshot): void {
    if (this.destroyed) {
      return;
    }
    const contentType = blob instanceof Blob ? blob.type.toLowerCase() : '';
    if (!(blob instanceof Blob) || !blob.size || contentType.indexOf('json') !== -1 ||
        contentType.indexOf('html') !== -1 || contentType.indexOf('text/') === 0) {
      if (fromApply) {
        this.showApplyError(blob);
      } else {
        this.onPreviewLoadError();
      }
      return;
    }
    if (fromApply) {
      // Error bodies can also arrive with an empty/octet-stream content type.
      // Check the PDF header before replacing or releasing the current file.
      const reader = new FileReader();
      reader.onload = () => {
        if (this.destroyed) {
          return;
        }
        if (String(reader.result || '').indexOf('%PDF-') < 0) {
          this.showApplyError(blob);
          return;
        }
        // Keep loading the original PDF if merging fails; cancel it only when the new file is accepted.
        if (this.initialPreview) {
          this.initialPreview.unsubscribe();
        }
        this.applying = false;
        this.replacePreview(blob);
        if (application) {
          this.appliedData = { ...application, pdf: blob };
        }
        this.toastr.success(this.message.getMessage('enterprise_contract.update_information_dialog.apply_success'));
      };
      reader.onerror = () => this.showApplyError(null);
      reader.readAsText(blob.slice(0, 1024));
    } else {
      this.replacePreview(blob);
    }
  }

  private replacePreview(blob: Blob): void {
    if (this.destroyed) {
      return;
    }
    const previousUrl = this.objectUrl;
    this.objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    this.pdfSrc = this.objectUrl;
    this.pageCurrent = 1;
    this.previewLoading = true;
    this.previewError = false;
    if (previousUrl) {
      URL.revokeObjectURL(previousUrl);
    }
  }

  private showApplyError(error: any): void {
    if (this.destroyed) {
      return;
    }
    this.appliedData = null;
    this.applying = false;
    this.previewLoading = false;
    // A failed merge does not invalidate the PDF already displayed.
    this.previewError = !this.pdfSrc;
    this.notifyError(error, 'enterprise_contract.update_information_dialog.preview_error');
  }

  private notifyError(error: any, fallback: string): void {
    const notify = (body: any) => {
      if (this.destroyed) {
        return;
      }
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body);
        } catch (_) { }
      }
      const detail = typeof body === 'string' ? body : body && body.message;
      this.toastr.error(typeof detail === 'string' && detail.trim() && detail.trim().charAt(0) !== '<'
        ? detail : this.message.getMessage(fallback));
    };
    const body = error && error.error != null ? error.error : error;
    if (body instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          notify(JSON.parse(String(reader.result || '')));
        } catch (_) {
          notify(null);
        }
      };
      reader.onerror = () => notify(null);
      reader.readAsText(body);
    } else {
      notify(body);
    }
  }

  onPreviewLoaded(): void {
    if (!this.destroyed && !this.applying) {
      this.previewLoading = false;
      this.previewError = false;
    }
  }

  onPreviewLoadError(): void {
    if (!this.destroyed && !this.applying) {
      this.appliedData = null;
      this.previewLoading = false;
      this.previewError = true;
    }
  }

  cancel(): void {
    if (this.destroyed || this.saving || this.cancelConfirmation) {
      return;
    }
    if (!this.formTemplate || !this.formTemplate.hasUnsavedChanges()) {
      this.dialogRef.close();
      return;
    }
    this.cancelConfirmation = this.dialog.open(WarningComponent, {
      width: '350px',
      data: 'messages.confirm_change_data'
    });
    this.subscriptions.add(this.cancelConfirmation.afterClosed().subscribe(confirmed => {
      this.cancelConfirmation = null;
      if (confirmed === true && !this.destroyed) {
        this.dialogRef.close();
      }
    }));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    if (this.cancelConfirmation) {
      this.cancelConfirmation.close(false);
      this.cancelConfirmation = null;
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }
}
